import React from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';

import {control, lineHeight, radius, space, type, universal} from '../../design-system/tokens';

import type {MenuAction, PromoTile, ResolvedGroup, ResolvedMenu} from './menu/menuTypes';
import {PANEL_WIDTH} from './breakpoints';
import {chromeType} from './chromeType';
import {headerChrome} from './headerChrome';
import {MenuLinkText} from './MenuLinkText';

const MAX_COLUMNS = 4;
// SHORTCUT: layout literals from header/dropdown/desk (204:27672); verdict "layout literal".
// Upgrade path: derive from the layout.desktop tokens once DIS-1735 adds them.
const COLUMN_WIDTH = 174;
const PROMO_GRID_WIDTH = 459;
// SHORTCUT: Figma draws the banner 459 × 222.67 (a scaled instance; verdict "artefact").
// Upgrade path: design redraws the banner frame at an integer size.
export const BANNER_ASPECT = 459 / 222.67;
// Figma's column gap is 13, off-scale (verdict "design error"); the row gap 12 is used for both.
const PROMO_GAP = space.s12;
const SQUARE_TILE = (PROMO_GRID_WIDTH - PROMO_GAP) / 2;

/**
 * Groups into at most 4 columns. When every group carries `column` (menuConfig.ts), that
 * decides, and a column left empty by dropped groups goes. Otherwise a column takes groups in
 * order until the next one would make it taller (in lines) than the tallest single group; the
 * last column takes the rest.
 */
export function packColumns(groups: ResolvedGroup[]): ResolvedGroup[][] {
  if (groups.length > 0 && groups.every(g => g.column !== undefined)) {
    const byColumn: ResolvedGroup[][] = [];
    for (const group of groups) {
      const c = Math.min(group.column ?? 0, MAX_COLUMNS - 1);
      (byColumn[c] ??= []).push(group);
    }
    return byColumn.filter(Boolean);
  }
  const lines = (g: ResolvedGroup) => 1 + g.items.length;
  const budget = Math.max(0, ...groups.map(lines));
  const columns: ResolvedGroup[][] = [];
  let used = 0;
  for (const group of groups) {
    const current = columns[columns.length - 1];
    if (current && (used + lines(group) <= budget || columns.length === MAX_COLUMNS)) {
      current.push(group);
      used += lines(group);
    } else {
      columns.push([group]);
      used = lines(group);
    }
  }
  return columns;
}

/** Shown while the departments load (Q3): the panel's shape, with no link text. */
const PLACEHOLDER_LINES = [0.9, 0.7, 0.8, 0.6, 0.75];
export const PanelPlaceholder = () => (
  <View
    testID="site-header-mega-menu-placeholder"
    style={styles.placeholder}
    accessibilityLabel="Loading menu"
    aria-busy={true}>
    {PLACEHOLDER_LINES.map((fraction, i) => (
      <View key={i} style={[styles.placeholderLine, {width: `${fraction * 100}%`}]} />
    ))}
  </View>
);

const Promo = ({tile, onAction}: {tile: PromoTile; onAction(a: MenuAction): void}) => {
  const width = tile.wide ? PROMO_GRID_WIDTH : SQUARE_TILE;
  const height = tile.wide ? PROMO_GRID_WIDTH / BANNER_ASPECT : SQUARE_TILE;
  const art = <tile.image width={width} height={height} />;
  const action = tile.action;
  if (!action) {
    return (
      <View style={[styles.promo, {width, height}]} accessibilityLabel={tile.label}>
        {art}
      </View>
    );
  }
  return (
    <Pressable
      onPress={() => onAction(action)}
      accessibilityRole="link"
      accessibilityLabel={tile.label}
      style={[styles.promo, {width, height}]}>
      {art}
    </Pressable>
  );
};

export type MegaMenuPanelProps = {
  menu: ResolvedMenu;
  /** Departments still loading (`useMegaMenus().isLoading`): placeholder columns, no promos. */
  loading?: boolean;
  /** The tallest the panel may be; taller content scrolls inside it (DesktopHeader's cap). */
  maxHeight?: number;
  onAction(a: MenuAction): void;
};

/**
 * The desktop dropdown of one menu (header/dropdown/desk 204:27672), ported from the header
 * prototype (ea875fae6 MegaMenuPanel.tsx): main links, a divider, group columns, promo grid.
 */
export const MegaMenuPanel = ({menu, loading = false, maxHeight, onAction}: MegaMenuPanelProps) => {
  if (loading) {
    return (
      <View
        testID="site-header-mega-menu-panel"
        style={[styles.panel, styles.panelContent]}
        accessibilityLabel={`${menu.label} menu`}>
        <View style={styles.columns}>
          {[0, 1, 2].map(c => (
            <View key={c} style={styles.column}>
              <PanelPlaceholder />
            </View>
          ))}
        </View>
      </View>
    );
  }
  const columns = packColumns(menu.groups);
  return (
    <ScrollView
      testID="site-header-mega-menu-panel"
      style={[styles.panel, {maxHeight}]}
      contentContainerStyle={styles.panelContent}
      accessibilityLabel={`${menu.label} menu`}>
      {menu.mainLinks.length > 0 && (
        <>
          <View style={styles.mainLinks}>
            {menu.mainLinks.map((link, i) => (
              <MenuLinkText key={i} link={link} style={styles.mainLink} onAction={onAction} />
            ))}
          </View>
          <View style={styles.divider} />
        </>
      )}
      <View style={styles.body}>
        <View style={styles.columns}>
          {columns.map((column, c) => (
            <View key={c} testID={`site-header-mega-menu-column-${c}`} style={styles.column}>
              {column.map((group, g) => (
                <View key={g} style={styles.group}>
                  <MenuLinkText link={group.heading} style={styles.heading} onAction={onAction} />
                  {group.items.map((item, i) => (
                    <MenuLinkText key={i} link={item} style={styles.item} onAction={onAction} />
                  ))}
                  {group.shopAll ? (
                    <MenuLinkText link={group.shopAll} style={styles.shopAll} onAction={onAction} />
                  ) : null}
                </View>
              ))}
            </View>
          ))}
        </View>
        {menu.promos.length > 0 && (
          <View testID="site-header-mega-menu-promos" style={styles.promoGrid}>
            {menu.promos.map((tile, i) => (
              <Promo key={i} tile={tile} onAction={onAction} />
            ))}
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  // Fills the header's panel layer up to 1288. No tablet dropdown is designed: below 1440
  // the promo grid wraps under the columns.
  panel: {
    width: '100%',
    maxWidth: PANEL_WIDTH,
    alignSelf: 'center',
    backgroundColor: universal.white,
    borderBottomLeftRadius: radius.card,
    borderBottomRightRadius: radius.card,
  },
  panelContent: {padding: space.s24, gap: space.s24},
  mainLinks: {gap: space.s24},
  mainLink: {...chromeType.label16SemiBold, color: headerChrome.accent},
  // spec.json assumes universal.greyMedium for this line.
  divider: {height: control.borderWidth, backgroundColor: universal.greyMedium},
  body: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: space.s16,
  },
  columns: {flexDirection: 'row', gap: space.s16},
  // Items fill the 174 column; Figma's fixed 131 forces wrapping (verdict "design error").
  column: {width: COLUMN_WIDTH, gap: space.s40},
  group: {gap: space.s24},
  heading: {...chromeType.label16Medium, color: universal.black},
  item: {...type.button.category, ...lineHeight.button.category, color: universal.black},
  shopAll: {...chromeType.label16Medium, color: headerChrome.accent},
  promoGrid: {
    width: PROMO_GRID_WIDTH,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PROMO_GAP,
    alignContent: 'flex-start',
  },
  promo: {borderRadius: radius.card, overflow: 'hidden'},
  placeholder: {gap: space.s16},
  // SHORTCUT: 16 tall bars, one per link line, in universal.greyMedium: a prototype skeleton
  // with no Figma frame (spec § 9.1 note 2). Upgrade path: design draws the loading panel.
  placeholderLine: {
    height: space.s16,
    borderRadius: radius.control,
    backgroundColor: universal.greyMedium,
  },
});
